import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SearchIcon, Hash, FileText } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pct } from "@/components/layernav/primitives";
import { fmtBig } from "@/components/sector/SectorHero";
import { EntityLogo } from "@/components/entity/EntityLink";

export const TYPE_STYLE: Record<string, string> = {
  protocol: "border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF]",
  token: "border-[#34D399]/40 bg-[#34D399]/10 text-[#34D399]",
  chain: "border-[#60A5FA]/40 bg-[#60A5FA]/10 text-[#60A5FA]",
  company: "border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA]",
  project: "border-white/25 bg-white/[0.06] text-gray-300",
};

const fmtPrice = (v: number) =>
  v >= 1000 ? v.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : v >= 1 ? v.toFixed(2) : v.toFixed(4);

/* Search box with typed canonical-entity suggestions (identity-separated:
   Aave · PROTOCOL ≠ AAVE · TOKEN), topics and articles. Entity rows are
   live-aware via the Entity Registry: live entities route to their canonical
   page; non-live entities fall back to a canonical NewsLayer search — never a
   dead row. When an Entity Intelligence Page ships, the registry flips `live`
   and rows re-route automatically. */
export const SearchBox = ({ input, setInput, onSubmit, isFetching }: {
  input: string; setInput: (v: string) => void; onSubmit: (e: React.FormEvent) => void; isFetching: boolean;
}) => {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const [debounced, setDebounced] = useState(input);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 200);
    return () => clearTimeout(t);
  }, [input]);

  const { data } = useQuery({
    queryKey: ["suggest", debounced],
    queryFn: async () => (await api.get("/search/suggest", { params: { q: debounced } })).data,
    enabled: debounced.length >= 2,
    placeholderData: (prev: any) => prev,
    staleTime: 30000,
  });

  const items = useMemo(() => {
    if (!data || debounced.length < 2) return [];
    return [
      ...(data.entities || []).map((e: any) => ({ kind: "entity", key: `e-${e.id}`, ...e })),
      ...(data.topics || []).map((t: string) => ({ kind: "topic", key: `t-${t}`, topic: t })),
      ...(data.articles || []).map((a: any) => ({ kind: "article", key: `a-${a.slug}`, ...a })),
    ];
  }, [data, debounced]);

  useEffect(() => setHi(-1), [items.length, debounced]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (it: any) => {
    setOpen(false);
    if (it.kind === "article") navigate(`/article/${it.slug}`);
    else if (it.kind === "entity") {
      // live-aware: canonical page when live, canonical search fallback otherwise
      if (it.live && it.route) navigate(it.route);
      else setInput(it.name);
    } else setInput(it.topic);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h <= 0 ? items.length - 1 : h - 1)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const showPanel = open && debounced.length >= 2 && items.length > 0;
  let idx = -1;
  const Row = ({ it }: { it: any }) => {
    idx += 1;
    const i = idx;
    const active = hi === i;
    return (
      <button type="button" role="option" aria-selected={active}
        data-testid={it.kind === "entity" ? `suggest-entity-${it.type}-${it.slug}` : `suggest-${it.kind}-${it.slug || it.topic}`}
        onMouseEnter={() => setHi(i)} onClick={() => pick(it)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${active ? "bg-white/[0.06]" : ""}`}>
        {it.kind === "entity" && (
          <>
            <EntityLogo type={it.type} slug={it.slug} name={it.name} size={22} />
            <span className="flex-1 min-w-0 flex items-baseline gap-1.5">
              <span className="font-bold text-[13px] text-white truncate">{it.name}</span>
              {it.symbol && <span className="font-mono text-[10px] text-gray-500 shrink-0">{it.symbol}</span>}
            </span>
            {it.fact?.kind === "tvl" && (
              <span className="font-mono text-[10.5px] text-gray-400 shrink-0">{fmtBig(it.fact.value)}</span>
            )}
            {it.fact?.kind === "price" && (
              <span className="flex items-baseline gap-1.5 shrink-0">
                <span className="font-mono text-[10.5px] text-gray-300">${fmtPrice(it.fact.value)}</span>
                {it.fact.change_24h_pct != null && <Pct v={it.fact.change_24h_pct} className="!text-[10px]" />}
              </span>
            )}
            <span className={`rounded border px-1.5 py-0.5 font-mono text-[7.5px] font-bold tracking-[0.16em] shrink-0 ${TYPE_STYLE[it.type] || TYPE_STYLE.project}`}>
              {String(it.type).toUpperCase()}
            </span>
          </>
        )}
        {it.kind === "topic" && (
          <>
            <Hash aria-hidden className="h-3.5 w-3.5 text-[#A78BFA] shrink-0" />
            <span className="text-[13px] text-white flex-1 truncate"><span className="capitalize">{it.topic}</span> stories</span>
            <span className="rounded border border-[#A78BFA]/40 bg-[#A78BFA]/10 px-1.5 py-0.5 font-mono text-[7.5px] font-bold tracking-[0.16em] text-[#A78BFA] shrink-0">TOPIC</span>
          </>
        )}
        {it.kind === "article" && (
          <>
            <FileText aria-hidden className="h-3.5 w-3.5 text-[#34D399] shrink-0" />
            <span className="text-[12.5px] text-gray-200 flex-1 truncate">{it.title}</span>
            <span className="font-mono text-[8.5px] uppercase tracking-wide text-gray-500 shrink-0">Article</span>
          </>
        )}
      </button>
    );
  };

  return (
    <div ref={wrapRef} className="relative max-w-2xl">
      <form onSubmit={(e) => { setOpen(false); onSubmit(e); }} className="flex gap-3" role="search">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          {isFetching && (
            <span data-testid="search-live-indicator" aria-hidden
              className="absolute right-3 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-[#00E5FF] animate-pulse motion-reduce:animate-none" />
          )}
          <Input data-testid="search-input" value={input} autoFocus role="combobox"
            aria-expanded={showPanel} aria-autocomplete="list"
            onChange={(e) => { setInput(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
            placeholder="Search articles, protocols, regulations…"
            className="pl-9 bg-[#0b0c10] border-white/[0.12] text-white" />
          {showPanel && (
            <div data-testid="search-suggest-panel" role="listbox"
              className="absolute left-0 right-0 top-full mt-2 z-30 rounded-xl border border-white/[0.10] bg-[#0b0c10] shadow-2xl shadow-black/60 overflow-hidden py-1.5">
              {data.entities?.length > 0 && (
                <p className="px-3 pt-1 pb-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-[#00E5FF]">Entities · Canonical</p>
              )}
              {(data.entities || []).map((e: any) => <Row key={`e-${e.id}`} it={{ kind: "entity", ...e }} />)}
              {data.topics?.length > 0 && (
                <p className="px-3 pt-2 pb-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-[#A78BFA]">Topics</p>
              )}
              {(data.topics || []).map((t: string) => <Row key={`t-${t}`} it={{ kind: "topic", topic: t }} />)}
              {data.articles?.length > 0 && (
                <p className="px-3 pt-2 pb-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-[#34D399]">Articles</p>
              )}
              {(data.articles || []).map((a: any) => <Row key={`a-${a.slug}`} it={{ kind: "article", ...a }} />)}
            </div>
          )}
        </div>
        <Button type="submit" data-testid="search-submit-button"
          className="bg-[#00E5FF] text-black font-bold hover:bg-white">Search</Button>
      </form>
    </div>
  );
};
